import { IChannelGroup, ISite } from '~/src/dash/api/console-api';
import { IDeal } from '~/src/dash/api/deals';

export function isNull(val: any) {
  return val === null || val === undefined;
}

function passthru<T>(v: T): T {
  return v;
}

export function parseNumber(
  _keys: string | string[],
  def?: number,
  transform?: (v?: number) => number | undefined,
) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        const v = parseInt(_fromObj[keys[i]], 10);
        if (!isNaN(v)) {
          return (transform || passthru)(v);
        }
      }
    }
    return (transform || passthru)(def);
  };
}

export function parseNumberAndRemoveComma(
  _keys: string | string[],
  def?: number,
  transform?: (v?: number) => number | undefined,
) {
  return (_fromObj: IObject) => {
    // build keys list
    const keys = Array.isArray(_keys) ? _keys : [_keys];

    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        // get the value of the current key
        const value = _fromObj[keys[i]];

        // check the type of value
        if (typeof value === 'string') {
          // if value is a string, remove the comma and parse as a number
          const v = parseInt(value.replace(/,/g, ''), 10);
          if (!isNaN(v)) {
            return (transform || passthru)(v);
          }
        } else {
          // if value is not a string, return as is
          return (transform || passthru)(value);
        }
      }
    }
    return (transform || passthru)(def);
  };
}

export function parseFloat(
  _keys: string | string[],
  def?: number,
  transform?: (v?: number) => number | undefined,
) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        const v = Number(_fromObj[keys[i]]);
        if (!isNaN(v)) {
          return (transform || passthru)(v);
        }
      }
    }
    return (transform || passthru)(def);
  };
}

export function isTrue(val: any) {
  const txt = ('' + val).trim().toLowerCase();
  if (txt === 'true' || txt === '1') {
    return true;
  }
  return false;
}

export function isFalse(val: any) {
  const txt = ('' + val).trim().toLowerCase();
  if (txt === 'false' || txt === '0') {
    return true;
  }
  return false;
}

export function parseBoolTrue(_keys: string | string[], transform?: (v: boolean) => any) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        if (isTrue(_fromObj[keys[i]])) {
          return (transform || passthru)(true);
        }
      }
    }
    return (transform || passthru)(false);
  };
}

export function parseBoolStringTrue(_keys: string | string[]) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        if (isTrue(_fromObj[keys[i]])) {
          return 'true';
        }
      }
    }
    return 'false';
  };
}

export function parseBoolFalse(_keys: string | string[]) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        if (isFalse(_fromObj[keys[i]])) {
          return false;
        }
      }
    }
    return true;
  };
}

export function parseString(_keys: string | string[], func?: string | ((fromObj: any) => any)) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (typeof _fromObj[keys[i]] === 'string') {
        if (typeof func === 'string') {
          return _fromObj[keys[i]].split(func).filter((item: string) => {
            return !!item;
          });
        }
        if (typeof func === 'function') {
          return func(_fromObj[keys[i]]);
        }
        return _fromObj[keys[i]];
      }
      if (keys[i] in _fromObj) {
        return '' + _fromObj[keys[i]];
      }
    }
    return '';
  };
}

export function parseNullString(_keys: string | string[], func?: string | ((fromObj: any) => any)) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (typeof _fromObj[keys[i]] === 'string') {
        if (typeof func === 'string') {
          return _fromObj[keys[i]].split(func).filter((item: string) => {
            return !!item;
          });
        }
        if (typeof func === 'function') {
          return func(_fromObj[keys[i]]);
        }
        return _fromObj[keys[i]];
      }
      if (keys[i] in _fromObj) {
        if (_fromObj[keys[i]] === null) {
          return null;
        }
        return '' + _fromObj[keys[i]];
      }
    }
    return '';
  };
}

export function parseDateTime(_keys: string | string[]) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (_fromObj[keys[i]]) {
        return _fromObj[keys[i]];
      }
    }
    return '';
  };
}

export function toString(
  _keys: string | string[],
  func?: string | ((fromObjValue: any) => string),
) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i] in _fromObj) {
        if (typeof func === 'function') {
          return func(_fromObj[keys[i]]);
        }
        if (typeof func === 'string' && Array.isArray(_fromObj[keys[i]])) {
          return _fromObj[keys[i]]
            .filter((item: string) => {
              return !!item;
            })
            .join(func);
        }
        return '' + _fromObj[keys[i]];
      }
    }
    return '';
  };
}

export function parseArray(
  _keys: string | string[],
  func: (fromObj: any) => any,
  sortFunc?: (a: any, b: any) => any,
) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (Array.isArray(_fromObj[keys[i]])) {
        return sortFunc ? _fromObj[keys[i]].map(func).sort(sortFunc) : _fromObj[keys[i]].map(func);
      }
    }
    return [];
  };
}

export function toArray(_keys: string | string[], transform?: (v?: any) => any | undefined) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (key in _fromObj && Array.isArray(_fromObj[key])) {
        return _fromObj[key].map((v: any) => {
          return (transform || passthru)(v);
        });
      }
    }
    return [];
  };
}

export function parseObject(_keys: string | string[], func: (fromObj: any) => any) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (_fromObj[keys[i]] !== null && typeof _fromObj[keys[i]] === 'object') {
        return func(_fromObj[keys[i]]);
      }
    }
    return {};
  };
}

const parseJSONParamsSafely = (str: string) => {
  if (str === '') {
    return {};
  }

  try {
    return JSON.parse(str);
  } catch (e) {
    console.warn('Error while parsing JSON: ', e);
    return {};
  }
};

export function parseJSON(_keys: string | string[]) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (_fromObj[keys[i]] !== null && typeof _fromObj[keys[i]] === 'string') {
        return parseJSONParamsSafely(_fromObj[keys[i]]);
      }
    }
    return {};
  };
}

// just return the TS object
export function parseObjectV2(_keys: string | string[]) {
  return (_fromObj: IObject) => {
    const keys = Array.isArray(_keys) ? _keys : [_keys];
    for (let i = 0; i < keys.length; i += 1) {
      if (_fromObj[keys[i]] !== null) {
        return _fromObj[keys[i]];
      }
    }
    return {};
  };
}

export function isValidObj(obj: any) {
  return !isNull(obj) && typeof obj === 'object';
}

export type ITranformFunc = (_fromObj: IObject, _modelKey: string) => any;

export interface ITransformConfig {
  priority: number;
  func: ITranformFunc;
}

export interface ITransformParsed {
  [key: string]: ITransformConfig;
}

export interface ITransform {
  [key: string]: ITransformConfig | ITranformFunc | string[] | string;
}

export type IObject = any;

function config(cfg?: ITransformConfig | ITranformFunc | string[] | string): ITransformConfig {
  if (isValidObj(cfg) && !Array.isArray(cfg)) {
    return cfg as ITransformConfig;
  }
  if (typeof cfg === 'function') {
    return {
      priority: 0,
      func: cfg as ITranformFunc,
    };
  }
  return {
    priority: 0,
    func: (_fromObj: IObject, _modelKey: string) => {
      if (cfg) {
        const keys = (Array.isArray(cfg) ? cfg : [cfg]) as string[];
        for (let i = 0; i < keys.length; i += 1) {
          if (keys[i] in _fromObj) {
            return _fromObj[keys[i]];
          }
        }
      }
      return _fromObj[_modelKey];
    },
  };
}

function createTransformModel(cfg: ITransform): ITransformParsed {
  const pa = {} as ITransformParsed;
  Object.keys(cfg).forEach((key: string) => {
    pa[key] = config(cfg[key]);
  });
  return pa;
}

export function transformModel<T extends IObject>(cfg: ITransform): (fromObj: any) => T {
  const modelTransformation = createTransformModel(cfg);
  return (fromObj: any) => {
    const model = {} as T as any;
    if (!isValidObj(fromObj)) {
      return model;
    }
    Object.keys(modelTransformation).forEach((modelKey: string) => {
      model[modelKey] = modelTransformation[modelKey].func(fromObj as IObject, modelKey);
    });
    return model;
  };
}

export const transformOnlyIds = transformModel<ISite | IChannelGroup | IDeal>({
  ID: parseNumber(['ID']),
});
